//dynamic-support-article-page.tsx

import React from "react";
import { useParams, useHistory, Link as RouterLink } from "react-router-dom";
import { Typography, Button, Box } from "@mui/material";
import { useSupportDataLoader } from "../contexts/support-data-loader";
import * as Sentry from "@sentry/react";
import Page from "../components/page";
import DOMPurify from "dompurify";
import { getCustomLabel } from "../utils/custom-labels";
import { UserContext } from "../components/app-routes";
import { useContext } from "react";

interface Params {
  category: string;
  article: string;
}

const DynamicSupportArticlePage: React.FC = () => {
  const { category, article } = useParams<Params>();
  const history = useHistory();
  const { supportData, isLoading } = useSupportDataLoader();
  const context = useContext(UserContext);
  const grower = context.grower;

  if (isLoading || supportData.length === 0) {
    return (
      <Page title={getCustomLabel(grower ?? null, "support")}>
        <Box sx={{ maxWidth: "800px", mx: "auto", px: 2, mt: 4 }}>
          <Typography variant="body1" color="textSecondary">
            Loading support article...
          </Typography>
        </Box>
      </Page>
    );
  }

  const categoryData = supportData.find(
    (cat) =>
      cat.slug?.toLowerCase() === category?.toLowerCase() ||
      cat.name.toLowerCase().replace(/\s+/g, "-") === category?.toLowerCase(),
  );

  const articleData = categoryData?.articles.find((a) => a.slug === article);

  if (!categoryData || !articleData) {
    Sentry.captureMessage(
      `Missing support article: category=${category}, article=${article}`,
      "warning",
    );

    return (
      <Page title={getCustomLabel(grower ?? null, "support")}>
        <Box sx={{ maxWidth: "800px", mx: "auto", px: 2, mt: 4 }}>
          <Typography variant="h4" color="error" gutterBottom>
            Article Not Found
          </Typography>
          <Typography>
            The article you’re looking for could not be found.
          </Typography>

          <Box mt={2}>
            <Button
              variant="contained"
              component={RouterLink}
              to="/home/support"
            >
              Back to Support Center
            </Button>
          </Box>
        </Box>
      </Page>
    );
  }

  return (
    <Page title={articleData.title}>
      <Box sx={{ maxWidth: "800px", mx: "auto", px: 2, pt: 1, pb: 2 }}>
        {/* Right-aligned Back button */}
        <Box display="flex" justifyContent="flex-end" mb={3}>
          <Button
            variant="outlined"
            onClick={() => history.push(`/home/support/${category}`)}
          >
            Back to Article List
          </Button>
        </Box>

        {/* Markdown-rendered Article */}
        <Box
          className="support-article-content"
          sx={{
            "& [data-youtube-video]": {
              position: "relative",
              width: "100%",
              paddingBottom: "56.25%",
              height: 0,
              overflow: "hidden",
              marginTop: "1.5rem",
              marginBottom: "1.5rem",
            },
            "& [data-youtube-video] iframe": {
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: 0,
            },
            "& img": { maxWidth: "100%", height: "auto" },
            "& table": {
              width: "100%",
              overflowX: "auto",
              display: "block",
              borderCollapse: "collapse",
            },
            "& th, & td": {
              padding: "8px",
              border: "1px solid #ccc",
              textAlign: "left",
            },
            "& p": { marginBottom: "1rem" },
            "& ul, & ol": { marginBottom: "1rem", paddingLeft: "1.4rem" },
            "& h1, & h2, & h3, & h4, & h5, & h6": {
              marginTop: "1.2rem",
              marginBottom: "0.6rem",
            },
          }}
        >
          <Box
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(articleData.content || "", {
                ADD_TAGS: ["iframe"],
                ADD_ATTR: [
                  "allow",
                  "allowfullscreen",
                  "autoplay",
                  "class",
                  "data-youtube-video",
                  "disablekbcontrols",
                  "enableiframeapi",
                  "endtime",
                  "frameborder",
                  "height",
                  "ivloadpolicy",
                  "loop",
                  "modestbranding",
                  "origin",
                  "playlist",
                  "rel",
                  "src",
                  "start",
                  "title",
                  "width",
                ],
              }),
            }}
          />
        </Box>
      </Box>
    </Page>
  );
};

export default DynamicSupportArticlePage;
